import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useTheme } from '../../Context/ThemeContext';
import { useLanguage } from '../../Context/LanguageContext';
import { getColor } from '../../Styles/theme';

import NewMemberReservationModal from '../../Model/Individual/ClassManageModel/ClassManageModal/NewMemberReservationModal';
import UserProfile from '../Normal/UserProfile';

export default function ClassInfoBox (props) {
  const { data, canceledSchedules, reserveData, reserveRendering, setReserveRendering } = props;

  const { isDark } = useTheme();
  const { t } = useLanguage();

  const [isClickedReserve, setIsClickedReserve] = useState(false);
  const [count, setCount] = useState(0);
  const [isCanceledSchedule, setIsCanceledSchedule] = useState(false);

  const InfoBoxRef = useRef();

  useEffect(() => {
    let isExistCount = false;
    let isExistCanceledSchedule = false;

    for (let i = 0; i < reserveData.length; i++) {
      if (data.scheduleId === reserveData[i].scheduleId) {
        isExistCount = true;
        setCount(reserveData[i].count);
        break;
      }
    }

    for (let i = 0 ; i < canceledSchedules.length; i++) {
      if (data.classTime === canceledSchedules[i].classTime
        && data.scheduleId === canceledSchedules[i].scheduleId) {
          isExistCanceledSchedule = true;
          setIsCanceledSchedule(true);
          break;
      }
    }

    if (!isExistCount) setCount(0);
    if (!isExistCanceledSchedule) setIsCanceledSchedule(false);
  }, [data, reserveData, canceledSchedules])

  function onClickClassData() {
    if (isClickedReserve) return;

    // 브레이크 타임일 경우 //
    if (data.classId === -1) return;

    setIsClickedReserve(true);
  }

  function renderTeacherName(name) {
    if (!name) return <TrainerName>{name}</TrainerName>;

    const match = name.match(/^(.+?)\s+Coach\s*외\s*(\d+)명$/);

    if (match) {
      return (
        <TrainerName>
          <TeacherNamePart>{match[1]}</TeacherNamePart>
          <TeacherSuffixPart>+ {match[2]}</TeacherSuffixPart>
        </TrainerName>
      );
    }

    const cleanName = name.replace(/\s+Coach$/, '');
    return <TrainerName><TeacherNamePart>{cleanName}</TeacherNamePart></TrainerName>;
  }

  return (
    <TopLevelWrapper onClick={onClickClassData}>
      { 
        isClickedReserve && 
          <NewMemberReservationModal 
            data={reserveData} 
            isCanceledSchedule={isCanceledSchedule}
            scheduleData={data} 
            reserveDataReLoad={reserveRendering} 
            setReserveDataReLoad={setReserveRendering} 
            cancel={() => setIsClickedReserve(false)}/> 
      }
      <InfoBox ref={InfoBoxRef} isCanceled={isCanceledSchedule} color={getColor(data.color, isDark)}>
        <ClassNameWrapper>
          <ClassName>{isCanceledSchedule ? data.className + ' ' + t('main.schedule.canceled') : (data.classId === -1 ? t('main.schedule.breakTime') : data.className)}</ClassName>
        </ClassNameWrapper>
        {
          data.classId === -1 ?
            <InfoWrapper>
              <TimeWrapper>
                <Time>{data.startTime} ~ {data.endTime}</Time>
              </TimeWrapper>
            </InfoWrapper>
            :
            <InfoWrapper>
              <TimeWrapper>
                <Time>{data.startTime} ~ {data.endTime}</Time>
                <TimeDivision />
                <Number>{t('main.schedule.reserved').replace('{count}', count)}</Number>
              </TimeWrapper>
              <TrainerWrapper>
                <TrainerImage>
                  <UserProfile profile={data.profile} defaultProfile='https://picture.link-zone.org/ZoneLogo.png'/>
                </TrainerImage>
                {renderTeacherName(data.teacherName)}
              </TrainerWrapper>
            </InfoWrapper>
        }
      </InfoBox>
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  margin: 0 0 0 0;

  width: 100%;
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;

  padding: 15px 20px;

  box-sizing: border-box;
  width: 100%;
  height: 80px;

  gap: 3px;

  border: ${props => props.isCanceled ? `1px dashed ${props.color}` : `1px solid ${props.theme.thirdBackgroundStroke}`};
  border-radius: 10px;
  background-color: ${props => props.theme.thirdBackground};

  transition: all 0.3s ease-in-out;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
      border-color: ${props => props.color};
    }
  }
`;

const ClassNameWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const ClassName = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 16px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const TimeWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 20px;
`;

const Time = styled.p`
  font-family: NotoSansKr-Medium;
  font-size: 14px;

  color: ${props => props.theme.subText};

  margin: 0 8px 0 0;

  width: 137px;
`;

const TimeDivision = styled.div`
  height: 14.5px;

  border-right: 2px solid ${props => props.theme.subText};
  border-radius: 10px;
`;

const Number = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.subText};

  margin: -3px 0 0 8px;
`;

const TrainerWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  min-width: 0;
  width: 180px;
`;

const TrainerImage = styled.div`
  flex-shrink: 0;

  width: 20px;
  height: 20px;

  border-radius: 20px;

  margin-right: 8px;

  overflow: hidden;

  & > * {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TrainerName = styled.p`
  display: flex;
  flex-direction: row;
  align-items: center;

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.subText};

  margin: 0;

  min-width: 0;

  gap: 4px;

  overflow: hidden;
`;

const TeacherNamePart = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
`;

const TeacherSuffixPart = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
`;
